import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const SacredEntry = () => {
  const [showContent, setShowContent] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [audioStarted, setAudioStarted] = useState(false);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Attempt autoplay — if it works, set state to playing
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = 0.3;
    const playPromise = audio.play();
    if (playPromise) {
      playPromise.then(() => {
        setAudioStarted(true);
        setIsMuted(false);
      }).catch(() => {
        // Autoplay blocked — wait for first user interaction to start
        const enableAudio = () => {
          if (audioRef.current) {
            audioRef.current.volume = 0.3;
            audioRef.current.play().then(() => {
              setAudioStarted(true);
              setIsMuted(false);
            }).catch(() => {});
          }
          document.removeEventListener("click", enableAudio);
          document.removeEventListener("keydown", enableAudio);
          document.removeEventListener("touchstart", enableAudio);
          document.removeEventListener("scroll", enableAudio);
        };
        document.addEventListener("click", enableAudio);
        document.addEventListener("keydown", enableAudio);
        document.addEventListener("touchstart", enableAudio);
        document.addEventListener("scroll", enableAudio);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAudio = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().then(() => setAudioStarted(true)).catch(() => {});
      setIsMuted(false);
    } else {
      audioRef.current.pause();
      setIsMuted(true);
    }
  };

  const handleEnter = () => {
    if (audioRef.current && !isMuted) {
      audioRef.current.volume = 0.1;
    }
    navigate("/contemplation");
  };

  return (
    <div 
      className="min-h-screen bg-cosmic-void flex items-center justify-center overflow-hidden relative"
      data-testid="sacred-entry-page"
    >
      {/* Background Stars */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-divine-gold rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.2, 1, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Om Audio */}
      <audio
        ref={audioRef}
        src="https://customer-assets.emergentagent.com/job_siwon-awareness-hub/artifacts/qkqq6c5m_konstantinpazuzustudio-soothing-relaxation-491002.mp3"
        loop
        preload="auto"
      />

      {/* Audio Toggle */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        onClick={toggleAudio}
        className={`absolute top-6 right-6 z-20 p-3 rounded-full border transition-colors bg-cosmic-deep/50 ${
          !audioStarted ? "border-divine-gold/60 animate-pulse" : "border-divine-gold/30 hover:border-divine-gold/60"
        }`}
        data-testid="audio-toggle"
      >
        {isMuted ? (
          <VolumeX className="w-6 h-6 text-divine-gold/50" />
        ) : (
          <Volume2 className="w-6 h-6 text-divine-gold" />
        )}
      </motion.button>

      {/* Main Content */}
      <div className="text-center z-10 px-6">
        {/* Breathing Om Symbol */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-8"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="text-8xl md:text-[130px] text-divine-gold glow-text font-heading select-none leading-none"
            data-testid="om-symbol"
          >
            ॐ
          </motion.div>
        </motion.div>

        {/* SIWON HARMONY Text */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 
            className="font-heading text-5xl md:text-7xl text-divine-gold tracking-wider mb-4 glow-text"
            data-testid="app-title"
          >
            SIWON HARMONY
          </h1>
          <p className="text-chakra-crown/70 text-lg md:text-xl tracking-wide font-body">
            The Vedic Way to Relationship and Life Transformation
          </p>
        </motion.div>

        {/* Loading/Entry Text */}
        {showContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="mt-12"
          >
            <p className="text-chakra-crown/60 text-sm tracking-[0.3em] uppercase mb-4">
              Entering Sacred Relationship and Life Wisdom Space...
            </p>
            <p className="text-divine-gold/80 text-base md:text-lg font-body tracking-wide mb-2">
              Hare Krsna Hare Krsna Krsna Krsna Hare Hare!
            </p>
            <p className="text-divine-gold/80 text-base md:text-lg font-body tracking-wide mb-8">
              Hare Ram Hare Ram Ram Ram Hare Hare !!
            </p>
            
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleEnter}
              className="vedic-button text-lg"
              data-testid="enter-button"
            >
              Begin Your Journey
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Mystic Overlay Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cosmic-void to-transparent pointer-events-none" />
    </div>
  );
};

export default SacredEntry;
